import { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import AppBackground from "../components/layout/AppBackground";


const sanitizeName = (value) => {
  return value
    .normalize("NFC") // conserva acentos bien formados
    .replace(/[^ \p{L}'-]/gu, ""); // solo letras unicode, espacio, ' y -
};

const toTitleCase = (value) => {
  return value
    .toLowerCase()
    .replace(/\p{L}[\p{L}'-]*/gu, (word) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    );
};

const sanitizeProfileName = (value) => {
  return value
    .normalize("NFC")
    .replace(/[^ \p{L}0-9_. -]/gu, ""); // permitís letras, números, _, ., espacio y -
};

const Signup = () => {
  const { t, i18n } = useTranslation("signup");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isCircular = location.pathname === "/circular";
  const isUNDelta = location.pathname === "/undelta";
  const firstNameRef = useRef();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    profile_name: "",
    email: "",
    password: "",
    confirm_password: "",
    gender: "m",
    language_preference: i18n.language?.startsWith("es") ? "es" : "en",
    source: isCircular ? "circular" : isUNDelta ? "undelta" : "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Normalización por campo
    if (name === "first_name" || name === "last_name") {
      const cleaned = toTitleCase(sanitizeName(value));
      setForm((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    if (name === "profile_name") {
      const cleaned = sanitizeProfileName(value);
      setForm((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    if (name === "email") {
      const cleaned = value.trim().toLowerCase();
      setForm((prev) => ({ ...prev, email: cleaned }));
      return;
    }

    if (name === "source") {
      const cleaned = value.trim().toLowerCase();
      setForm((prev) => ({ ...prev, source: cleaned }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.confirm_password
      ) {
      setError(t("required_fields_error"));
      return;
    }

    // Email regex simple y efectiva
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Usamos SIEMPRE el email normalizado
    const normalizedEmail = form.email.trim().toLowerCase();

    if (!emailRegex.test(normalizedEmail)) {
      setError(t("invalid_email"));
      return;
    }

    // Validar mínimo 4 caracteres
    if (form.password.length < 4) {
      setError(t("invalid_password"));
      return;
    }
    if (form.password !== form.confirm_password) {
      setError(t("password_mismatch"));
      return;
    }

    // Normalización final
    const first_name = toTitleCase(sanitizeName(form.first_name.trim()));
    const last_name = toTitleCase(sanitizeName(form.last_name.trim()));
    const email = normalizedEmail;
    const profile_name =
      form.profile_name.trim() !== ""
        ? sanitizeProfileName(form.profile_name.trim())
        : first_name;

    const source = (form.source || "").trim().toLowerCase();

    const payload = {
      ...form, // incluye todos los campos, names, password, gender, language_preference, etc.
      first_name, // sobrescribe con valores normalizados
      last_name,
      email,
      profile_name,
      source,
    };

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        navigate("/verify-email?status=check_email");
      } else {
        setError(data.msg || t("signup_failed"));
      }
    } catch (err) {
      setError(t("network_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppBackground
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          backgroundColor: "#212121",
          color: "#fff",
          borderRadius: 4,
          mt: 3,
          mb: 8,
          p: 4,
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 0 20px rgba(0,0,0,0.5)",
        }}
      >
        {/* LOGO DEL FORM */}
        {isCircular ? (
          // Muestra Logo Circular
          <>
            <img
              src="/assets/circular-logo-bar.png"
              alt="Circular"
              style={{ height: 60, marginBottom: 12 }}
            />
            <Typography variant="subtitle1" sx={{ mb: 4 }}>
              Circular te invita a registrarte en Pitchest
            </Typography>
          </>
        ) : (
          isUNDelta ? (
            // Muestra Logo UNDelta
            <>
              <img
                src="/assets/undelta-logo.svg"
                alt="UNDelta"
                style={{ height: 60, marginBottom: 12 }}
              />
              <Typography variant="subtitle1" sx={{ mb: 4 }}>
                Te invita a registrarte en Pitchest
              </Typography>
            </>
          ) : (
            //Muestra Logo PITCHEST
            <img
              src="/assets/logo-icon.png"
              alt="Pitchest Logo"
              style={{ height: 80, marginBottom: 16 }}
            />
          )
        )}

        <Typography variant="h5" gutterBottom>
          {t("signup_title")}
        </Typography>

        <TextField
          label={t("first_name")}
          name="first_name"
          value={form.first_name}
          autoComplete="given-name"
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          inputRef={firstNameRef}
          inputProps={{ maxLength: 50 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t("last_name")}
          name="last_name"
          value={form.last_name}
          autoComplete="family-name"
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ maxLength: 50 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t("profile_name")}
          name="profile_name"
          value={form.profile_name}
          autoComplete="given-name"
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ maxLength: 50 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t("email")}
          name="email"
          autoComplete="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ maxLength: 50 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t("password")}
          name="password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ maxLength: 50 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t("confirm_password")}
          name="confirm_password"
          autoComplete="new-password"
          type="password"
          value={form.confirm_password}
          onChange={handleChange}
          fullWidth
          margin="normal"
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel
            id="gender-label"
            shrink
            sx={{
              color: "#eee",
              mb: 0.5, // 👈 subimos el label para evitar solapamiento
            }}
          >
            {t("gender")}
          </InputLabel>
          <Select
            labelId="gender-label"
            id="gender"
            name="gender"
            value={form.gender}
            onChange={handleChange}
            label={t("gender")}
            sx={{
              color: "#fff",
              ".MuiSelect-icon": { color: "#45434" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#45434" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#A855F7",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#fff", // fondo del menú desplegable
                  color: "#222", // color del texto
                  "& .MuiMenuItem-root": {
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#eee",
                    color: "#555",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "#ddd",
                  },
                },
              },
            }}
        >
            <MenuItem value="m">{t("gender_m")}</MenuItem>
            <MenuItem value="f">{t("gender_f")}</MenuItem>
            <MenuItem value="u">{t("gender_u")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel
            id="language-label"
            shrink
            sx={{
              color: "#eee",
              mb: 0.5, // 👈 subimos el label para evitar solapamiento
            }}
          >
            {t("language_preference")}
          </InputLabel>
          <Select
            labelId="language-label"
            id="language_preference"
            name="language_preference"
            value={form.language_preference}
            onChange={handleChange}
            label={t("language_preference")}
            sx={{
              color: "#fff",
              ".MuiSelect-icon": { color: "#45434" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#45434" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#A855F7",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#fff", // fondo del menú desplegable
                  color: "#222", // color del texto
                  "& .MuiMenuItem-root": {
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#eee",
                    color: "#555",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "#ddd",
                  },
                },
              },
            }}
        >
            <MenuItem value="en">{t("english")}</MenuItem>
            <MenuItem value="es">{t("spanish")}</MenuItem>
          </Select>
        </FormControl>

        {(isCircular || isUNDelta) ? (

          <TextField
            label={t("source")}
            name="source"
            value={
              isCircular
                ? t("source_circular")
                : t("source_undelta")
            }
            disabled
            fullWidth
            margin="normal"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#fff",
                opacity: 1,
              },
              "& .MuiInputLabel-root": {
                color: "#eee",
              },
              "& .Mui-disabled .MuiInputLabel-root": {
                color: "#eee",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#454345",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#A855F7",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#A855F7",
              },
            }}
          />

        ) : (

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel
              id="source-label"
              shrink
              sx={{ color: "#eee", mb: 0.5 }}
            >
              {t("source")}
            </InputLabel>

            <Select
              labelId="source-label"
              id="source"
              name="source"
              value={form.source}
              onChange={handleChange}
              label={t("source")}
              sx={{
                color: "#fff",
                ".MuiSelect-icon": { color: "#45434" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#45434" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#A855F7",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#222",
                    "& .MuiMenuItem-root": { fontWeight: 500 },
                    "& .Mui-selected": {
                      backgroundColor: "#eee",
                      color: "#555",
                    },
                    "& .Mui-selected:hover": {
                      backgroundColor: "#ddd",
                    },
                  },
                },
              }}
            >
              <MenuItem value="linkedin">{t("source_linkedin")}</MenuItem>
              <MenuItem value="google">{t("source_google")}</MenuItem>
              <MenuItem value="recommendation">{t("source_recommendation")}</MenuItem>
              <MenuItem value="other">{t("source_other")}</MenuItem>
            </Select>
          </FormControl>

        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            mt: 2,
            borderRadius: 999,
            py: 1.2,
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.7)", // texto más claro
              backgroundColor: "rgba(168,85,247,0.4)" // opcional: fondo más visible
            }
          }}
        >
          {loading ? t("signing_up") : t("signup_button")}
        </Button>

        <Button
          onClick={() => navigate("/login")}
          sx={{ mt: 2, color: theme.palette.primary.light }}
        >
          {t("go_to_login")}
        </Button>
      </Box>
    </AppBackground>
  );
};

export default Signup;
